import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import { wilmaSAVCustomerId } from "../../config";
import useSavAnalytics from "../../data/useSavAnalytics";
import useWilmaCustomers from "../../data/useWilmaCustomers";
import LoadingWheel from "../../components/shared/LoadingWheel";
import {
  IPostcodeAreaStats,
  ISourceCustomerStats,
} from "../../models/SavAnalytics";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type Preset = "today" | "thisWeek" | "last7" | "thisMonth" | "thisYear";

function getRange(preset: Preset): { from: Date; to: Date } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "thisWeek": {
      const day = today.getDay();
      const diff = day === 0 ? 6 : day - 1;
      const monday = new Date(today);
      monday.setDate(today.getDate() - diff);
      return { from: monday, to: today };
    }
    case "last7": {
      const from = new Date(today);
      from.setDate(today.getDate() - 6);
      return { from, to: today };
    }
    case "thisMonth":
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: today };
    case "thisYear":
      return { from: new Date(now.getFullYear(), 0, 1), to: today };
  }
}

const PRESETS: { label: string; value: Preset }[] = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "thisWeek" },
  { label: "Last 7 Days", value: "last7" },
  { label: "This Month", value: "thisMonth" },
  { label: "This Year", value: "thisYear" },
];

function percent(part: number, total: number): string {
  // Tolerates a field the API has not shipped yet, so a mid-rollout page shows 0% rather than NaN%.
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return "0%";
  return `${Math.round((part / total) * 1000) / 10}%`;
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  // A field the API has not shipped yet arrives undefined; show 0 rather than an empty card.
  const shown = typeof value === "number" && !Number.isFinite(value) ? 0 : (value ?? 0);
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 flex flex-col gap-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-3xl font-bold text-gray-800">{shown}</p>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

type SortDir = "asc" | "desc";

function sortRows<T>(rows: T[], key: keyof T, dir: SortDir): T[] {
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (typeof av === "number" && typeof bv === "number") {
      return dir === "asc" ? av - bv : bv - av;
    }
    const as = String(av ?? "");
    const bs = String(bv ?? "");
    return dir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
  });
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
  align = "left",
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none ${
        align === "right" ? "text-right" : "text-left"
      }`}
      onClick={onClick}
    >
      {label}
      {active ? (dir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );
}

export default function SavAnalytics() {
  const { setInfo } = usePageNameContext();
  const { loading, customerId } = useAuth();
  const isSav = customerId === wilmaSAVCustomerId;
  const [activePreset, setActivePreset] = useState<Preset>("thisMonth");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const { from, to } = getRange(activePreset);
  const sourceCustomerId = sourceFilter === "all" ? null : sourceFilter;
  const { statsQuery, stats } = useSavAnalytics(from, to, isSav, sourceCustomerId);
  const { customers } = useWilmaCustomers();
  const [areaQuery, setAreaQuery] = useState("");
  const [sourceQuery, setSourceQuery] = useState("");
  const [areaSort, setAreaSort] = useState<{ key: keyof IPostcodeAreaStats; dir: SortDir }>({
    key: "quoteCount",
    dir: "desc",
  });
  const [sourceSort, setSourceSort] = useState<{
    key: "name" | keyof ISourceCustomerStats;
    dir: SortDir;
  }>({
    key: "quoteCount",
    dir: "desc",
  });

  const sourceOptions = useMemo(() => {
    const names = customers.data?.data ?? [];
    const savId = wilmaSAVCustomerId?.toLowerCase();
    const options = names
      .map((c) => ({
        id: c.id,
        name:
          c.id.toLowerCase() === savId ? "SellAVehicle (widget)" : c.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    if (savId && !options.some((o) => o.id.toLowerCase() === savId)) {
      options.unshift({ id: wilmaSAVCustomerId!, name: "SellAVehicle (widget)" });
    }

    return options;
  }, [customers.data]);

  const customerNames = useMemo(() => {
    const map = new Map<string, string>();
    customers.data?.data.forEach((c) => map.set(c.id.toLowerCase(), c.name));
    return map;
  }, [customers.data]);

  const sourceLabel = (id: string | null) => {
    if (!id || id === "marketplace") return "SellAVehicle (marketplace)";
    if (id.toLowerCase() === wilmaSAVCustomerId?.toLowerCase()) return "SellAVehicle (widget)";
    return customerNames.get(id.toLowerCase()) ?? "Unknown customer";
  };

  const extraSourceOptions = useMemo(() => {
    const known = new Set(sourceOptions.map((o) => o.id.toLowerCase()));
    return (stats?.bySourceCustomer ?? [])
      .map((row) => row.sourceCustomerId)
      .filter((id): id is string => !!id && !known.has(id.toLowerCase()))
      .map((id) => ({ id, name: sourceLabel(id) }));
  }, [sourceOptions, stats, customerNames]);

  useEffect(() => {
    setInfo({
      name: "Analytics",
      desc:
        sourceFilter === "all"
          ? "Quotes by postcode area and source"
          : `Quotes for ${sourceLabel(sourceFilter === "marketplace" ? null : sourceFilter)}`,
    });
  }, [setInfo, sourceFilter, customerNames]);

  const filteredAreas = useMemo(() => {
    if (!stats) return [];
    const q = areaQuery.trim().toUpperCase();
    const rows = q
      ? stats.byPostcodeArea.filter((r) => r.area.toUpperCase().includes(q))
      : stats.byPostcodeArea;
    return sortRows(rows, areaSort.key, areaSort.dir);
  }, [stats, areaQuery, areaSort]);

  const sourceRows = useMemo(() => {
    if (!stats) return [];
    return stats.bySourceCustomer.map((row) => ({
      ...row,
      name: sourceLabel(row.sourceCustomerId),
    }));
  }, [stats, customerNames]);

  const filteredSources = useMemo(() => {
    const q = sourceQuery.trim().toLowerCase();
    const rows = q
      ? sourceRows.filter(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            (r.sourceCustomerId ?? "marketplace").toLowerCase().includes(q)
        )
      : sourceRows;

    return [...rows].sort((a, b) => {
      const key = sourceSort.key;
      const av = a[key];
      const bv = b[key];
      if (typeof av === "number" && typeof bv === "number") {
        return sourceSort.dir === "asc" ? av - bv : bv - av;
      }
      const as = String(av ?? "");
      const bs = String(bv ?? "");
      return sourceSort.dir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
  }, [sourceRows, sourceQuery, sourceSort]);

  const toggleAreaSort = (key: keyof IPostcodeAreaStats) => {
    setAreaSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }
    );
  };

  const toggleSourceSort = (key: "name" | keyof ISourceCustomerStats) => {
    setSourceSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }
    );
  };

  const chartData = useMemo(() => {
    const top = (stats?.byPostcodeArea ?? []).slice(0, 15);
    return {
      labels: top.map((r) => r.area),
      datasets: [
        {
          label: "Quotes",
          data: top.map((r) => r.quoteCount),
          backgroundColor: "rgba(79, 70, 229, 0.75)",
          borderColor: "rgba(79, 70, 229, 1)",
          borderWidth: 1,
          borderRadius: 4,
        },
        {
          label: "Completed",
          data: top.map((r) => r.completedCount),
          backgroundColor: "rgba(16, 185, 129, 0.75)",
          borderColor: "rgba(16, 185, 129, 1)",
          borderWidth: 1,
          borderRadius: 4,
        },
        {
          label: "Handed to call centre",
          data: top.map((r) => r.handedToCallCentreCount),
          backgroundColor: "rgba(14, 165, 233, 0.75)",
          borderColor: "rgba(14, 165, 233, 1)",
          borderWidth: 1,
          borderRadius: 4,
        },
        {
          label: "Out of coverage",
          data: top.map((r) => r.outOfCoverageCount),
          backgroundColor: "rgba(245, 158, 11, 0.75)",
          borderColor: "rgba(245, 158, 11, 1)",
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [stats]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      title: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
      },
    },
  };

  if (loading) return <LoadingWheel />;
  if (!isSav) return <Navigate to="/" replace />;

  return (
    <div className="mt-4 max-w-7xl">
      <div className="flex gap-2 mb-6 flex-wrap items-center">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            onClick={() => setActivePreset(p.value)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              activePreset === p.value
                ? "bg-primary-600 border-primary-600 text-white"
                : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
            }`}
          >
            {p.label}
          </button>
        ))}
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="ml-0 sm:ml-2 rounded-full border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:border-gray-400"
        >
          <option value="all">All sources</option>
          <option value="marketplace">SellAVehicle (marketplace)</option>
          {sourceOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
          {extraSourceOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </div>

      {statsQuery.isLoading ? (
        <LoadingWheel />
      ) : statsQuery.isError ? (
        <p className="text-red-500">Failed to load analytics.</p>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:grid-cols-3 lg:grid-cols-4">
            <StatCard label="Total Quotes" value={stats.totalQuotes} />
            <StatCard
              label="Completed"
              value={stats.completedCount}
              hint={`${percent(stats.completedCount, stats.totalQuotes)} · customer finished it themselves`}
            />
            <StatCard
              label="Handed To Call Centre"
              value={stats.handedToCallCentreCount}
              hint={`${percent(stats.handedToCallCentreCount, stats.totalQuotes)} · lapsed, passed on to ring`}
            />
            <StatCard
              label="Quote Selected"
              value={stats.quoteSelectedCount}
              hint={`${percent(stats.quoteSelectedCount, stats.totalQuotes)} · picked collection or drop-off`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8 sm:grid-cols-3 lg:grid-cols-4">
            <StatCard
              label="Converted (all leads)"
              value={stats.convertedCount}
              hint={`${percent(stats.convertedCount, stats.totalQuotes)} · completed + handed over`}
            />
            <StatCard
              label="Out of Coverage"
              value={stats.outOfCoverageCount}
              hint={`${percent(stats.outOfCoverageCount, stats.totalQuotes)} · no operator covers the postcode`}
            />
            <StatCard
              label="No Postcode"
              value={stats.noPostcodeCount}
              hint={`${percent(stats.noPostcodeCount, stats.totalQuotes)} · left before giving one`}
            />
            <StatCard label="Sources" value={stats.bySourceCustomer.length} />
          </div>

          <p className="text-xs text-gray-500 mb-8">
            A quote that goes quiet after the contact step is handed to a call centre, which creates a
            lead without the customer having accepted anything. Those are counted under{" "}
            <span className="font-semibold">Handed To Call Centre</span>, so a source with a lot of
            them will show a high converted rate without the customers having chosen to sell.{" "}
            <span className="font-semibold">Quote Selected</span> leaves handoffs out entirely, which
            undercounts it a little: roughly one in nine is a customer who did pick an option and then
            lapsed.
          </p>

          {stats.byPostcodeArea.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-5 mb-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-4">
                Top postcode areas
              </h2>
              <div className="relative h-64 sm:h-80">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-5 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Quotes by postcode area
                </h2>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={areaQuery}
                    onChange={(e) => setAreaQuery(e.target.value)}
                    placeholder="Filter area"
                    className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md w-full sm:w-44"
                  />
                </div>
              </div>
              <div className="overflow-x-auto max-h-[32rem]">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-200">
                      <SortHeader
                        label="Area"
                        active={areaSort.key === "area"}
                        dir={areaSort.dir}
                        onClick={() => toggleAreaSort("area")}
                      />
                      <SortHeader
                        label="Quotes"
                        active={areaSort.key === "quoteCount"}
                        dir={areaSort.dir}
                        onClick={() => toggleAreaSort("quoteCount")}
                        align="right"
                      />
                      <SortHeader
                        label="Completed"
                        active={areaSort.key === "completedCount"}
                        dir={areaSort.dir}
                        onClick={() => toggleAreaSort("completedCount")}
                        align="right"
                      />
                      <SortHeader
                        label="Call centre"
                        active={areaSort.key === "handedToCallCentreCount"}
                        dir={areaSort.dir}
                        onClick={() => toggleAreaSort("handedToCallCentreCount")}
                        align="right"
                      />
                      <SortHeader
                        label="Out of coverage"
                        active={areaSort.key === "outOfCoverageCount"}
                        dir={areaSort.dir}
                        onClick={() => toggleAreaSort("outOfCoverageCount")}
                        align="right"
                      />
                      <SortHeader
                        label="No postcode"
                        active={areaSort.key === "noPostcodeCount"}
                        dir={areaSort.dir}
                        onClick={() => toggleAreaSort("noPostcodeCount")}
                        align="right"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAreas.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                          No quotes in this range.
                        </td>
                      </tr>
                    ) : (
                      filteredAreas.map((row) => (
                        <tr key={row.area} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 font-semibold text-gray-900">{row.area}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.quoteCount}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.completedCount}
                            <span className="ml-1 text-xs text-gray-400">
                              {percent(row.completedCount, row.quoteCount)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.handedToCallCentreCount}
                            <span className="ml-1 text-xs text-gray-400">
                              {percent(row.handedToCallCentreCount, row.quoteCount)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.outOfCoverageCount}
                            <span className="ml-1 text-xs text-gray-400">
                              {percent(row.outOfCoverageCount, row.quoteCount)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.noPostcodeCount}
                            <span className="ml-1 text-xs text-gray-400">
                              {percent(row.noPostcodeCount, row.quoteCount)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-5 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Quotes by source
                </h2>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={sourceQuery}
                    onChange={(e) => setSourceQuery(e.target.value)}
                    placeholder="Filter source"
                    className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md w-full sm:w-44"
                  />
                </div>
              </div>
              <div className="overflow-x-auto max-h-[32rem]">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-200">
                      <SortHeader
                        label="Source"
                        active={sourceSort.key === "name"}
                        dir={sourceSort.dir}
                        onClick={() => toggleSourceSort("name")}
                      />
                      <SortHeader
                        label="Quotes"
                        active={sourceSort.key === "quoteCount"}
                        dir={sourceSort.dir}
                        onClick={() => toggleSourceSort("quoteCount")}
                        align="right"
                      />
                      <SortHeader
                        label="Completed"
                        active={sourceSort.key === "completedCount"}
                        dir={sourceSort.dir}
                        onClick={() => toggleSourceSort("completedCount")}
                        align="right"
                      />
                      <SortHeader
                        label="Call centre"
                        active={sourceSort.key === "handedToCallCentreCount"}
                        dir={sourceSort.dir}
                        onClick={() => toggleSourceSort("handedToCallCentreCount")}
                        align="right"
                      />
                      <SortHeader
                        label="Out of coverage"
                        active={sourceSort.key === "outOfCoverageCount"}
                        dir={sourceSort.dir}
                        onClick={() => toggleSourceSort("outOfCoverageCount")}
                        align="right"
                      />
                      <SortHeader
                        label="No postcode"
                        active={sourceSort.key === "noPostcodeCount"}
                        dir={sourceSort.dir}
                        onClick={() => toggleSourceSort("noPostcodeCount")}
                        align="right"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSources.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                          No quotes in this range.
                        </td>
                      </tr>
                    ) : (
                      filteredSources.map((row) => {
                        const rowFilter = row.sourceCustomerId ?? "marketplace";
                        const selected = sourceFilter === rowFilter;
                        return (
                        <tr
                          key={rowFilter}
                          className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                            selected ? "bg-primary-50" : ""
                          }`}
                          onClick={() => setSourceFilter(rowFilter)}
                          title="Show analytics for this source only"
                        >
                          <td className="px-3 py-2">
                            <div className="font-semibold text-gray-900">{row.name}</div>
                            <div className="text-xs text-gray-400 font-mono break-all">
                              {row.sourceCustomerId ?? "—"}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums align-top">
                            {row.quoteCount}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums align-top">
                            {row.completedCount}
                            <span className="ml-1 text-xs text-gray-400">
                              {percent(row.completedCount, row.quoteCount)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums align-top">
                            {row.handedToCallCentreCount}
                            <span className="ml-1 text-xs text-gray-400">
                              {percent(row.handedToCallCentreCount, row.quoteCount)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums align-top">
                            {row.outOfCoverageCount}
                            <span className="ml-1 text-xs text-gray-400">
                              {percent(row.outOfCoverageCount, row.quoteCount)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums align-top">
                            {row.noPostcodeCount}
                            <span className="ml-1 text-xs text-gray-400">
                              {percent(row.noPostcodeCount, row.quoteCount)}
                            </span>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
